import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './PlayAgainBanner.css';

/**
 * Modal som visas efter matchslut för spelare som var med i matchen.
 *
 * Frågar "Vill du ställa dig i kön igen?" med Ja/Nej-knappar och nedräkning.
 * Klickar man Ja köas man direkt med förtur via API-endpointen
 * (/api/queue/{guildId}/ready-check/{matchId}/respond) och modalen byter till
 * en bekräftelse med OK-knapp som stänger ner overlayen så sidan blir fri
 * att navigera. Nej eller utgången tid plockar inte bort spelaren från något
 * — den lägger bara till declined-status och låter modalen försvinna.
 *
 * @param {object} props
 * @param {string} props.guildId       Discord-guild som matchen tillhör
 * @param {string} props.matchId       Match-id, samma som ready check-nyckeln
 * @param {number} [props.timeout=90]  Sekunder till timeout (default 90, men eventet styr)
 * @param {function} [props.onDismiss] Anropas när modalen ska stängas helt (rensar parent-state)
 */
export default function PlayAgainBanner({ guildId, matchId, timeout = 90, onDismiss }) {
  const { t } = useTranslation();
  const { user, isLoggedIn, getAuthHeaders } = useAuth();
  const [status, setStatus] = useState('pending'); // pending | accepted | declined | expired
  const [timeLeft, setTimeLeft] = useState(timeout);
  const [reminded, setReminded] = useState(false);
  const [shake, setShake] = useState(false);
  const intervalRef = useRef(null);

  // Countdown
  useEffect(() => {
    if (status !== 'pending') return;
    intervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          setStatus('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [status]);

  // Reminder shake at 30s left
  useEffect(() => {
    if (status === 'pending' && timeLeft === 30 && !reminded) {
      setReminded(true);
      setShake(true);
      setTimeout(() => setShake(false), 1000);
    }
  }, [timeLeft, status, reminded]);

  // Auto-dismiss on expired (after short delay)
  useEffect(() => {
    if (status === 'expired' || status === 'declined') {
      const timer = setTimeout(() => {
        if (onDismiss) onDismiss();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [status, onDismiss]);

  const handleAccept = useCallback(async () => {
    if (!isLoggedIn || !guildId || !matchId) return;
    try {
      await fetch(`${API_URL}/api/queue/${guildId}/ready-check/${matchId}/respond`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: 'accepted' }),
      });
      setStatus('accepted');
    } catch (err) {
      console.error('Accept error:', err);
    }
  }, [isLoggedIn, guildId, matchId, getAuthHeaders]);

  const handleDecline = useCallback(async () => {
    if (!isLoggedIn || !guildId || !matchId) return;
    try {
      await fetch(`${API_URL}/api/queue/${guildId}/ready-check/${matchId}/respond`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: 'declined' }),
      });
    } catch (err) {
      console.error('Decline error:', err);
    }
    setStatus('declined');
  }, [isLoggedIn, guildId, matchId, getAuthHeaders]);

  if (!isLoggedIn || !matchId) return null;
  if (status === 'declined') return null;

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const isUrgent = timeLeft <= 30;

  if (status === 'expired') {
    return (
      <div className="play-again-overlay">
        <div className="play-again-banner expired">
          <div className="play-again-content">
            <div className="play-again-text">
              <span className="play-again-title">{t('queue.timeUp')}</span>
              <span className="play-again-subtitle">{t('queue.notInQueue')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="play-again-overlay">
      <div className={`play-again-banner ${status} ${shake ? 'shake' : ''}`}>
        <div className="play-again-content">
          {status === 'pending' ? (
            <>
              <div className="play-again-text">
                <span className="play-again-title">{t('queue.playAgainQ')}</span>
                <span className={`play-again-timer ${isUrgent ? 'urgent' : ''}`}>
                  {minutes}:{seconds.toString().padStart(2, '0')}
                </span>
                {isUrgent && (
                  <span className="play-again-reminder">{t('queue.answerBeforeTime')}</span>
                )}
              </div>
              <div className="play-again-actions">
                <button className="play-again-btn accept" onClick={handleAccept}>
                  {t('queue.yes')}
                </button>
                <button className="play-again-btn decline" onClick={handleDecline}>
                  {t('queue.no')}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="play-again-text">
                <span className="play-again-title">{t('queue.inQueueAgain')}</span>
                <span className="play-again-subtitle">
                  {t('queue.priorityNext')}
                </span>
              </div>
              <div className="play-again-actions">
                <button
                  className="play-again-btn accept"
                  onClick={() => onDismiss && onDismiss()}
                >
                  {t('queue.ok')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
